import React from 'react';
import { X, Mail, Edit2, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface UserEditModalProps {
  isEditUserModalOpen: boolean;
  setIsEditUserModalOpen: (open: boolean) => void;
  userForm: {
    name: string;
    firstName: string;
    email: string;
    address: {
      street: string;
      postalCode: string;
      city: string;
      country: string;
    };
  };
  setUserForm: (form: any) => void;
  isSaving: boolean;
  handleSaveUserProfile: (e: React.FormEvent) => void;
}

const UserEditModal: React.FC<UserEditModalProps> = ({
  isEditUserModalOpen,
  setIsEditUserModalOpen,
  userForm,
  setUserForm,
  isSaving,
  handleSaveUserProfile
}) => {
  const { t } = useTranslation();
  if (!isEditUserModalOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/60 z-[110] flex items-end p-4 backdrop-blur-sm">
      <div className="bg-white w-full max-w-md mx-auto p-8 rounded-t-[3rem] animate-slide-up relative shadow-2xl max-h-[90vh] overflow-y-auto">
        <h3 className="text-2xl font-black mb-6">{t('app:profile.edit_profile')}</h3>
        <button onClick={() => setIsEditUserModalOpen(false)} className="absolute top-8 right-8 text-gray-300 hover:text-gray-500">
          <X />
        </button>
        
        <form onSubmit={handleSaveUserProfile} className="space-y-4">
          <div>
            <label className="text-[10px] font-black uppercase text-gray-400 flex items-center gap-2">
              <Edit2 size={12}/> {t('app:profile.first_name')}
            </label>
            <input 
              type="text" 
              value={userForm.firstName} 
              onChange={e => setUserForm({...userForm, firstName: e.target.value})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none mt-2" 
              placeholder={t('app:profile.first_name_placeholder')} 
            />
          </div>
          
          <div>
            <label className="text-[10px] font-black uppercase text-gray-400 flex items-center gap-2">
              <Edit2 size={12}/> {t('app:profile.last_name')}
            </label>
            <input 
              type="text" 
              value={userForm.name} 
              onChange={e => setUserForm({...userForm, name: e.target.value})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none mt-2" 
              placeholder={t('app:profile.last_name_placeholder')} 
            />
          </div>

          <div>
            <label className="text-[10px] font-black uppercase text-gray-400 flex items-center gap-2">
              <Mail size={12}/> {t('app:profile.email')}
            </label>
            <input 
              type="email" 
              value={userForm.email} 
              disabled
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none mt-2 opacity-60" 
            />
          </div>

          <div className="space-y-1">
            <label className="text-[10px] font-black uppercase text-gray-400 flex items-center gap-2">
              <Mail size={12}/> {t('app:profile.address')}
            </label>
            <input 
              type="text" 
              value={userForm.address.street} 
              onChange={e => setUserForm({...userForm, address: {...userForm.address, street: e.target.value}})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none mb-2" 
              placeholder={t('app:profile.street_placeholder')} 
            />
            <div className="grid grid-cols-2 gap-2">
              <input 
                type="text" 
                value={userForm.address.postalCode} 
                onChange={e => setUserForm({...userForm, address: {...userForm.address, postalCode: e.target.value}})} 
                className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
                placeholder={t('app:profile.postal_code_placeholder')} 
              />
              <input 
                type="text" 
                value={userForm.address.city} 
                onChange={e => setUserForm({...userForm, address: {...userForm.address, city: e.target.value}})} 
                className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none" 
                placeholder={t('app:profile.city_placeholder')} 
              />
            </div>
            <select
              value={userForm.address.country}
              onChange={e => setUserForm({...userForm, address: {...userForm.address, country: e.target.value}})} 
              className="w-full p-4 bg-gray-50 border rounded-2xl font-bold outline-none mt-2"
            >
              <option value="">{t('app:profile.select_country')}</option>
              <option value="Afghanistan">Afghanistan</option>
              <option value="Afrique du Sud">Afrique du Sud</option>
              <option value="Albanie">Albanie</option>
              <option value="Algérie">Algérie</option>
              <option value="Allemagne">Allemagne</option>
              <option value="Andorre">Andorre</option>
              <option value="Angola">Angola</option>
              <option value="Antigua-et-Barbuda">Antigua-et-Barbuda</option>
              <option value="Arabie Saoudite">Arabie Saoudite</option>
              <option value="Argentine">Argentine</option>
              <option value="Arménie">Arménie</option>
              <option value="Australie">Australie</option>
              <option value="Autriche">Autriche</option>
              <option value="Azerbaïdjan">Azerbaïdjan</option>
              <option value="Bahamas">Bahamas</option>
              <option value="Bahreïn">Bahreïn</option>
              <option value="Bangladesh">Bangladesh</option>
              <option value="Barbade">Barbade</option>
              <option value="Belgique">Belgique</option>
              <option value="Belize">Belize</option>
              <option value="Bénin">Bénin</option>
              <option value="Bhoutan">Bhoutan</option>
              <option value="Biélorussie">Biélorussie</option>
              <option value="Bolivie">Bolivie</option>
              <option value="Bosnie-Herzégovine">Bosnie-Herzégovine</option>
              <option value="Botswana">Botswana</option>
              <option value="Brésil">Brésil</option>
              <option value="Brunei">Brunei</option>
              <option value="Bulgarie">Bulgarie</option>
              <option value="Burkina Faso">Burkina Faso</option>
              <option value="Burundi">Burundi</option>
              <option value="Cambodge">Cambodge</option>
              <option value="Cameroun">Cameroun</option>
              <option value="Canada">Canada</option>
              <option value="Cap-Vert">Cap-Vert</option>
              <option value="Chili">Chili</option>
              <option value="Chine">Chine</option>
              <option value="Chypre">Chypre</option>
              <option value="Colombie">Colombie</option>
              <option value="Comores">Comores</option>
              <option value="Congo">Congo</option>
              <option value="Corée du Nord">Corée du Nord</option>
              <option value="Corée du Sud">Corée du Sud</option>
              <option value="Costa Rica">Costa Rica</option>
              <option value="Côte d'Ivoire">Côte d'Ivoire</option>
              <option value="Croatie">Croatie</option>
              <option value="Cuba">Cuba</option>
              <option value="Danemark">Danemark</option>
              <option value="Djibouti">Djibouti</option>
              <option value="Dominique">Dominique</option>
              <option value="Égypte">Égypte</option>
              <option value="Émirats arabes unis">Émirats arabes unis</option>
              <option value="Équateur">Équateur</option>
              <option value="Érythrée">Érythrée</option>
              <option value="Espagne">Espagne</option>
              <option value="Estonie">Estonie</option>
              <option value="États-Unis">États-Unis</option>
              <option value="Ethiopie">Ethiopie</option>
              <option value="Fidji">Fidji</option>
              <option value="Finlande">Finlande</option>
              <option value="France">France</option>
              <option value="Gabon">Gabon</option>
              <option value="Gambie">Gambie</option>
              <option value="Géorgie">Géorgie</option>
              <option value="Ghana">Ghana</option>
              <option value="Grèce">Grèce</option>
              <option value="Grenade">Grenade</option>
              <option value="Guatemala">Guatemala</option>
              <option value="Guinée">Guinée</option>
              <option value="Guinée équatoriale">Guinée équatoriale</option>
              <option value="Guinée-Bissau">Guinée-Bissau</option>
              <option value="Guyana">Guyana</option>
              <option value="Haïti">Haïti</option>
              <option value="Honduras">Honduras</option>
              <option value="Hongrie">Hongrie</option>
              <option value="Inde">Inde</option>
              <option value="Indonésie">Indonésie</option>
              <option value="Iran">Iran</option>
              <option value="Iraq">Iraq</option>
              <option value="Irlande">Irlande</option>
              <option value="Islande">Islande</option>
              <option value="Israël">Israël</option>
              <option value="Italie">Italie</option>
              <option value="Jamaïque">Jamaïque</option>
              <option value="Japon">Japon</option>
              <option value="Jordanie">Jordanie</option>
              <option value="Kazakhstan">Kazakhstan</option>
              <option value="Kenya">Kenya</option>
              <option value="Kirghizistan">Kirghizistan</option>
              <option value="Kiribati">Kiribati</option>
              <option value="Koweït">Koweït</option>
              <option value="Laos">Laos</option>
              <option value="Lesotho">Lesotho</option>
              <option value="Lettonie">Lettonie</option>
              <option value="Liban">Liban</option>
              <option value="Libéria">Libéria</option>
              <option value="Libye">Libye</option>
              <option value="Liechtenstein">Liechtenstein</option>
              <option value="Lituanie">Lituanie</option>
              <option value="Luxembourg">Luxembourg</option>
              <option value="Macédoine">Macédoine</option>
              <option value="Madagascar">Madagascar</option>
              <option value="Malaisie">Malaisie</option>
              <option value="Malawi">Malawi</option>
              <option value="Maldives">Maldives</option>
              <option value="Mali">Mali</option>
              <option value="Malte">Malte</option>
              <option value="Maroc">Maroc</option>
              <option value="Marshall">Marshall</option>
              <option value="Maurice">Maurice</option>
              <option value="Mauritanie">Mauritanie</option>
              <option value="Mexique">Mexique</option>
              <option value="Micronésie">Micronésie</option>
              <option value="Moldavie">Moldavie</option>
              <option value="Monaco">Monaco</option>
              <option value="Mongolie">Mongolie</option>
              <option value="Mozambique">Mozambique</option>
              <option value="Myanmar">Myanmar</option>
              <option value="Namibie">Namibie</option>
              <option value="Nauru">Nauru</option>
              <option value="Népal">Népal</option>
              <option value="Nicaragua">Nicaragua</option>
              <option value="Niger">Niger</option>
              <option value="Nigeria">Nigeria</option>
              <option value="Niue">Niue</option>
              <option value="Norvège">Norvège</option>
              <option value="Nouvelle-Zélande">Nouvelle-Zélande</option>
              <option value="Oman">Oman</option>
              <option value="Ouganda">Ouganda</option>
              <option value="Ouzbékistan">Ouzbékistan</option>
              <option value="Pakistan">Pakistan</option>
              <option value="Palaos">Palaos</option>
              <option value="Panama">Panama</option>
              <option value="Papouasie-Nouvelle-Guinée">Papouasie-Nouvelle-Guinée</option>
              <option value="Paraguay">Paraguay</option>
              <option value="Pays-Bas">Pays-Bas</option>
              <option value="Pérou">Pérou</option>
              <option value="Philippines">Philippines</option>
              <option value="Pologne">Pologne</option>
              <option value="Portugal">Portugal</option>
              <option value="Qatar">Qatar</option>
              <option value="Roumanie">Roumanie</option>
              <option value="Royaume-Uni">Royaume-Uni</option>
              <option value="Russie">Russie</option>
              <option value="Rwanda">Rwanda</option>
              <option value="Saint-Christophe-et-Niévès">Saint-Christophe-et-Niévès</option>
              <option value="Sainte-Lucie">Sainte-Lucie</option>
              <option value="Saint-Marin">Saint-Marin</option>
              <option value="Saint-Vincent-et-les Grenadines">Saint-Vincent-et-les Grenadines</option>
              <option value="Salomon">Salomon</option>
              <option value="Salvador">Salvador</option>
              <option value="Samoa">Samoa</option>
              <option value="Sao Tomé-et-Principe">Sao Tomé-et-Principe</option>
              <option value="Sénégal">Sénégal</option>
              <option value="Serbie">Serbie</option>
              <option value="Seychelles">Seychelles</option>
              <option value="Sierra Leone">Sierra Leone</option>
              <option value="Singapour">Singapour</option>
              <option value="Slovaquie">Slovaquie</option>
              <option value="Slovénie">Slovénie</option>
              <option value="Somalie">Somalie</option>
              <option value="Soudan">Soudan</option>
              <option value="Sri Lanka">Sri Lanka</option>
              <option value="Suisse">Suisse</option>
              <option value="Suriname">Suriname</option>
              <option value="Swaziland">Swaziland</option>
              <option value="Syrie">Syrie</option>
              <option value="Tadjikistan">Tadjikistan</option>
              <option value="Tanzanie">Tanzanie</option>
              <option value="Tchad">Tchad</option>
              <option value="Thaïlande">Thaïlande</option>
              <option value="Togo">Togo</option>
              <option value="Tonga">Tonga</option>
              <option value="Trinité-et-Tobago">Trinité-et-Tobago</option>
              <option value="Tunisie">Tunisie</option>
              <option value="Turkménistan">Turkménistan</option>
              <option value="Turquie">Turquie</option>
              <option value="Tuvalu">Tuvalu</option>
              <option value="Ukraine">Ukraine</option>
              <option value="Uruguay">Uruguay</option>
            </select>
          </div>

          <button 
            disabled={isSaving} 
            type="submit" 
            className="w-full bg-indigo-600 text-white py-4 rounded-[1.5rem] font-black shadow-xl mt-4"
          >
            {isSaving ? <Loader2 className="mx-auto animate-spin" size={20} /> : t('common:buttons.save')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default UserEditModal;
